import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import { MainLayout } from '../../components/layout/MainLayout';

interface Residence {
  id: number;
  rgi: string;
  name: string;
  block: string;
  floor: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  confirmed?: boolean;
}

interface CommercialService {
  id: number;
  name: string;
}

/**
 * AdminResidenceUsersPage - Cadastro de moradores de um condomínio
 * Equivalente a: admin/residence_users_registration/edit.html.erb
 */
export default function AdminResidenceUsersPage() {
  const { t } = useTranslation();
  const { condominiumId } = useParams<{ condominiumId: string }>();
  const [residences, setResidences] = useState<Residence[]>([]);
  const [commercialService, setCommercialService] = useState<CommercialService | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editEmail, setEditEmail] = useState('');

  useEffect(() => {
    const fetchResidences = async () => {
      try {
        // TODO: Criar endpoint API para buscar residências do condomínio
        const response = await api.get(`/api/v1/admin/commercial_services/${condominiumId}/residences`);
        setResidences(response.data.residences);
        setCommercialService(response.data.commercial_service);
      } catch (err) {
        console.error('Error fetching residences:', err);
        setError(t('views.admin.residence_users.load_error', 'Erro ao carregar residências'));
        // Dados mock para desenvolvimento
        setResidences([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (condominiumId) {
      fetchResidences();
    }
  }, [condominiumId]);

  const handleEditEmail = useCallback((residence: Residence) => {
    setEditingId(residence.id);
    setEditEmail(residence.email || '');
  }, []);

  const handleSaveEmail = useCallback(async (residenceId: number) => {
    try {
      await api.patch(`/api/v1/admin/residences/${residenceId}`, {
        email: editEmail
      });
      
      setResidences(prev => prev.map(r => 
        r.id === residenceId ? { ...r, email: editEmail } : r
      ));
      setEditingId(null);
      setEditEmail('');
    } catch (err) {
      console.error('Error saving email:', err);
      alert(t('views.admin.residence_users.save_error', 'Erro ao salvar email'));
    }
  }, [editEmail]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent, residenceId: number) => {
    if (e.key === 'Enter') {
      handleSaveEmail(residenceId);
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditEmail('');
    }
  }, [handleSaveEmail]);

  return (
    <MainLayout>
      <div className="flex flex-wrap">
        <div className="w-full bg-white rounded-lg shadow border border-[#337ab7]">
          <div className="bg-[#337ab7] text-white px-4 py-3 rounded-t-lg font-medium">
            {t('views.admin.residence_users.title', 'Cadastro de moradores')} {commercialService && `- ${commercialService.name}`}
          </div>
          <div className="p-4">
            <p>
              {t('views.admin.residence_users.intro', 'Para cadastrar basta preencher o campo email e pressionar ENTER. Em poucos segundos a planilha será salva automaticamente. Após a confirmação do morador e cadastramento da senha os campos nome e sobrenome estarão preenchidos.')}
            </p>

            {isLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#337ab7] mx-auto" role="status">
                  <span className="sr-only">{t('common.loading', 'Carregando...')}</span>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
            ) : (
              <div className="overflow-x-auto mt-4">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left border-b">RGI</th>
                      <th className="p-2 text-left border-b">{t('views.admin.residence_users.th_block', 'Bloco')}</th>
                      <th className="p-2 text-left border-b">{t('views.admin.residence_users.th_unit', 'Unidade')}</th>
                      <th className="p-2 text-left border-b">{t('views.devise.registrations.email', 'Email')}</th>
                      <th className="p-2 text-left border-b">{t('views.devise.registrations.name', 'Nome')}</th>
                      <th className="p-2 text-left border-b">{t('views.devise.registrations.last_name', 'Sobrenome')}</th>
                      <th className="p-2 text-left border-b">{t('views.admin.residence_users.th_status', 'Status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {residences.length > 0 ? (
                      residences.map((residence) => (
                        <tr key={residence.id} className="odd:bg-gray-50 hover:bg-gray-100">
                          <td className="p-2 border-b">{residence.rgi}</td>
                          <td className="p-2 border-b">{residence.block}</td>
                          <td className="p-2 border-b">{residence.name}</td>
                          <td className="p-2 border-b">
                            {editingId === residence.id ? (
                              <input
                                type="email"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-[#80bdff] focus:outline-none"
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                onKeyDown={(e) => handleKeyPress(e, residence.id)}
                                onBlur={() => handleSaveEmail(residence.id)}
                                autoFocus
                              />
                            ) : (
                              <span
                                className="cursor-pointer hover:text-[#337ab7]"
                                onClick={() => handleEditEmail(residence)}
                              >
                                {residence.email || <em className="text-gray-500">{t('views.admin.residence_users.click_to_edit', 'Clique para editar')}</em>}
                              </span>
                            )}
                          </td>
                          <td className="p-2 border-b">{residence.firstName || '-'}</td>
                          <td className="p-2 border-b">{residence.lastName || '-'}</td>
                          <td className="p-2 border-b">
                            {residence.confirmed ? (
                              <span className="inline-block px-2 py-1 text-xs font-medium text-white bg-green-500 rounded">{t('views.admin.residence_users.status_confirmed', 'Confirmado')}</span>
                            ) : residence.email ? (
                              <span className="inline-block px-2 py-1 text-xs font-medium text-gray-800 bg-yellow-400 rounded">{t('views.admin.residence_users.status_pending', 'Pendente')}</span>
                            ) : (
                              <span className="inline-block px-2 py-1 text-xs font-medium text-white bg-gray-500 rounded">{t('views.admin.residence_users.status_not_registered', 'Não cadastrado')}</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center text-gray-500 p-4">
                          {t('views.admin.residence_users.none_found', 'Nenhuma residência encontrada')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
